// Lib
import React, { useLayoutEffect, useRef } from 'react';

// Selectors
import {
    isEditPreviewAttachmentFetched,
    isEditPreviewAttachmentUploading,
} from '../../../../attachments/attachmentsSelector';

// Components
import ImageModalV2Editor from './ImageModalV2Editor';

import { intercomTrackEvent } from '../../../../../analytics/intercomService';

// Types
import { ImageModalV2EditModeProps } from '../imageModalV2Types';
import type { ImageModalV2OperationToastState } from '../toolbar/imageModalV2OperationToastTypes';

type ImageModalV2CropModeContentProps = ImageModalV2EditModeProps & {
    boundsWidth: number;
    boundsHeight: number;
    onEditorReadyChange?: (ready: boolean) => void;
    onOperationToastChange: (toast: ImageModalV2OperationToastState | null) => void;
};

/**
 * V2 crop entry point: maps the measured V2 image bounds onto editor viewport props.
 */
const ImageModalV2CropModeContent: React.FC<ImageModalV2CropModeContentProps> = ({
    boundsWidth,
    boundsHeight,
    gridSize,
    onEditorReadyChange,
    onOperationToastChange,
    ...editModeProps
}) => {
    const { attachment, finishEditingMode, finishEditingAfterSave } = editModeProps;
    const isAwaitingEditPreviewCompletionRef = useRef(false);

    useLayoutEffect(() => {
        if (isEditPreviewAttachmentUploading(attachment)) {
            isAwaitingEditPreviewCompletionRef.current = true;
            return;
        }

        // Attachment completion reveals the canvas preview. Switching the modal in a layout effect puts both
        // surfaces on that preview before the browser can paint either one; failed or cancelled uploads never
        // set `fetched` and leave the editor open.
        if (!isAwaitingEditPreviewCompletionRef.current || !isEditPreviewAttachmentFetched(attachment)) return;

        isAwaitingEditPreviewCompletionRef.current = false;
        finishEditingMode();
    }, [attachment, finishEditingMode]);

    const closeEditorCallback = (didSave?: boolean, options?: { viewPreviewReady?: boolean }) => {
        intercomTrackEvent('edited-image');

        // When a changed image was persisted, keep the frozen editor frame visible until the view image has
        // loaded the newly-saved source. When the view is already rendering the saved image (the upload's
        // local preview) there is nothing to wait for — switch in the same batch so the modal and the board
        // canvas update in the same paint.
        if (didSave && !options?.viewPreviewReady) {
            finishEditingAfterSave();
        } else {
            finishEditingMode();
        }
    };

    return (
        <ImageModalV2Editor
            {...editModeProps}
            gridSize={gridSize}
            editorBounds={{ width: boundsWidth, height: boundsHeight }}
            cancelEditing={finishEditingMode}
            afterSaveCb={closeEditorCallback}
            onEditorReadyChange={onEditorReadyChange}
            onOperationToastChange={onOperationToastChange}
        />
    );
};

export default ImageModalV2CropModeContent;
